import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Container, Page } from '@ui';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { Working } from '@ui/Working';
import { useDispatch } from 'react-redux';
import { showErrorBanner } from '@utils/application';
import { getKeyValue, updateMemoryKeyValue } from '@services/agentMemory';
import { JsonTable } from '@features/JsonEditor/components/JsonTable';

const TableSection = styled.div`
  margin-top: 16px;
  overflow-x: auto;
`;

const PasteTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.component};
  color: ${({ theme }) => theme.themeColors.text};
  resize: vertical;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.themeColors.primaryColor || '#4a9eff'};
  }

  &::placeholder {
    color: ${({ theme }) => theme.themeColors.secondaryText || '#999'};
  }
`;

const ErrorText = styled(Body5)`
  color: ${({ theme }) => theme.themeColors.error || '#e74c3c'};
  margin-top: 8px;
`;

const ValidationText = styled(Body5)`
  color: ${({ $isValid, theme }) =>
    $isValid ? theme.themeColors.success || '#27ae60' : theme.themeColors.error || '#e74c3c'};
  margin-top: 8px;
`;

const PasteButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;

  button {
    padding: 6px 16px;
  }
`;

const RawJsonTextarea = styled.textarea`
  width: 100%;
  min-height: 400px;
  padding: 12px;
  font-family: 'SF Mono', 'Consolas', 'Monaco', monospace;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.component};
  color: ${({ theme }) => theme.themeColors.text};
  resize: vertical;
  outline: none;
  tab-size: 2;

  &:focus {
    border-color: ${({ theme }) => theme.themeColors.primaryColor || '#4a9eff'};
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

export const MemoryKeyEditor = ({ memoryKey, userId, onGoBack, onSaveSuccess }) => {
  const dispatch = useDispatch();
  const [keyData, setKeyData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [pasteMode, setPasteMode] = React.useState(false);
  const [pasteValue, setPasteValue] = React.useState('');
  const [pasteError, setPasteError] = React.useState(null);
  const [rawMode, setRawMode] = React.useState(false);
  const [rawValue, setRawValue] = React.useState('');
  const [rawError, setRawError] = React.useState(null);

  const isDataEmpty = (data) => {
    if (data == null || data === '') return true;
    if (Array.isArray(data) && data.length === 0) return true;
    if (typeof data === 'object' && Object.keys(data).length === 0) return true;
    return false;
  };

  const normalizeKeyData = (value) => {
    if (typeof value !== 'string') return value;

    try {
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  };

  const getJsonValidation = (value) => {
    if (!value.trim()) return null;

    try {
      JSON.parse(value);
      return {
        isValid: true,
        message: 'Valid JSON',
      };
    } catch (error) {
      return {
        isValid: false,
        message: `Invalid JSON: ${error.message}`,
      };
    }
  };

  const pasteValidation = getJsonValidation(pasteValue);
  const rawValidation = getJsonValidation(rawValue);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const value = await getKeyValue(memoryKey);
      const normalizedValue = normalizeKeyData(value);

      if (!cancelled) {
        setKeyData(normalizedValue);
        setLoading(false);
        if (isDataEmpty(normalizedValue)) {
          setPasteMode(true);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [memoryKey]);

  const handleTableUpdate = (path, newValue) => {
    setKeyData((prev) => {
      const updated = structuredClone(prev);
      if (path.length === 0) return newValue;

      let current = updated;
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      current[path[path.length - 1]] = newValue;
      return updated;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const success = await updateMemoryKeyValue(memoryKey, JSON.stringify(keyData), userId);
    if (success) {
      setHasChanges(false);
      onSaveSuccess();
    } else {
      showErrorBanner('Something went wrong, please try again.', null, dispatch);
    }
    setSaving(false);
  };

  const handleOpenPaste = () => {
    setPasteMode(true);
    setPasteValue('');
    setPasteError(null);
  };

  const handleCancelPaste = () => {
    setPasteMode(false);
    setPasteValue('');
    setPasteError(null);
  };

  const handleApplyPaste = () => {
    try {
      const parsed = JSON.parse(pasteValue);
      setKeyData(parsed);
      setHasChanges(true);
      setPasteMode(false);
      setPasteValue('');
      setPasteError(null);
    } catch (e) {
      setPasteError(e.message);
    }
  };

  const handleEnterRawMode = () => {
    setRawValue(JSON.stringify(keyData, null, 2));
    setRawError(null);
    setRawMode(true);
  };

  const handleExitRawMode = () => {
    try {
      const parsed = JSON.parse(rawValue);
      setKeyData(parsed);
      setHasChanges(true);
      setRawMode(false);
      setRawError(null);
    } catch (e) {
      setRawError(e.message);
    }
  };

  const handleDiscardRaw = () => {
    setRawMode(false);
    setRawError(null);
  };

  return (
    <Page width="2000px">
      <BackArrowButton onClick={onGoBack} />
      <Container top={40}>
        <Body1 bold>Update Key: {memoryKey}</Body1>
        <Container top={16}>
          {loading ? (
            <Working />
          ) : (
            <>
              {pasteMode ? (
                <Container top={8}>
                  <Body5>Paste JSON to overwrite the current value:</Body5>
                  <Container top={8}>
                    <PasteTextarea
                      value={pasteValue}
                      onChange={(e) => {
                        setPasteValue(e.target.value);
                        setPasteError(null);
                      }}
                      placeholder="Paste JSON here..."
                    />
                  </Container>
                  {pasteValidation && (
                    <ValidationText $isValid={pasteValidation.isValid}>{pasteValidation.message}</ValidationText>
                  )}
                  {pasteError && <ErrorText>Invalid JSON: {pasteError}</ErrorText>}
                  <PasteButtonRow>
                    <FlatButton onClick={handleApplyPaste} disabled={!pasteValue.trim()}>
                      Apply
                    </FlatButton>
                    <TransparentFlatButton onClick={handleCancelPaste}>Cancel</TransparentFlatButton>
                  </PasteButtonRow>
                </Container>
              ) : rawMode ? (
                <Container top={8}>
                  <RawJsonTextarea
                    value={rawValue}
                    onChange={(e) => {
                      setRawValue(e.target.value);
                      setRawError(null);
                    }}
                  />
                  {rawValidation && (
                    <ValidationText $isValid={rawValidation.isValid}>{rawValidation.message}</ValidationText>
                  )}
                  {rawError && <ErrorText>Invalid JSON: {rawError}</ErrorText>}
                  <PasteButtonRow>
                    <FlatButton onClick={handleExitRawMode}>Apply</FlatButton>
                    <TransparentFlatButton onClick={handleDiscardRaw}>Discard</TransparentFlatButton>
                  </PasteButtonRow>
                </Container>
              ) : (
                <>
                  <ToolbarRow>
                    <TransparentFlatButton onClick={handleOpenPaste}>SHOW JSON OVERWRITE INPUT</TransparentFlatButton>
                    <TransparentFlatButton onClick={handleEnterRawMode}>Edit Raw JSON</TransparentFlatButton>
                  </ToolbarRow>
                  <TableSection>
                    <JsonTable data={keyData} path={[]} onUpdate={handleTableUpdate} depth={0} />
                  </TableSection>
                </>
              )}
              <Container top={24} style={{ textAlign: 'center' }}>
                <FlatButton disabled={saving || !hasChanges} fullWidth onClick={handleSave}>
                  {saving ? 'Saving...' : 'Save'}
                </FlatButton>
              </Container>
            </>
          )}
        </Container>
      </Container>
    </Page>
  );
};
